export type MembershipPlan = 'free' | 'plus' | 'pro' | 'studio'

export type MembershipFeature =
  | 'communityView'
  | 'communityPublish'
  | 'inAppRuntime'
  | 'download'
  | 'outsideApp'
  | 'priorityQueue'
  | 'earlyAccess'

export interface SubscriptionPlanPolicy {
  id: MembershipPlan
  name: string
  monthlyGenerations: number
  monthlyPriceUsd: number
  annualPriceUsd: number
  description: string
  features: readonly MembershipFeature[]
  featured?: boolean
}

export const membershipPolicyVersion = '2026-09-27.images.1'

const commonFeatures = [
  'communityView',
  'communityPublish',
  'inAppRuntime',
] as const satisfies readonly MembershipFeature[]

export const subscriptionPlans = [
  {
    id: 'free',
    name: 'Free',
    monthlyGenerations: 3,
    monthlyPriceUsd: 0,
    annualPriceUsd: 0,
    description: 'Tek seferlik 3 ücretsiz AI görsel üretim hakkı.',
    features: commonFeatures,
    featured: false,
  },
  {
    id: 'plus',
    name: 'Pro',
    monthlyGenerations: 75,
    monthlyPriceUsd: 6.99,
    annualPriceUsd: 69.99,
    description: 'Ayda 75 yüksek kalite görsel üretimi.',
    features: [...commonFeatures, 'download', 'outsideApp'],
    featured: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyGenerations: 75,
    monthlyPriceUsd: 6.99,
    annualPriceUsd: 69.99,
    description: 'Ayda 75 yüksek kalite görsel üretimi.',
    features: [
      ...commonFeatures,
      'download',
      'outsideApp',
    ],
    featured: false,
  },
  {
    id: 'studio',
    name: 'Studio',
    monthlyGenerations: 200,
    monthlyPriceUsd: 12.99,
    annualPriceUsd: 129.99,
    description: 'Ayda 200 profesyonel görsel üretimi ve öncelikli üretim kuyruğu.',
    features: [
      ...commonFeatures,
      'download',
      'outsideApp',
      'priorityQueue',
      'earlyAccess',
    ],
    featured: true,
  },
] as const satisfies readonly SubscriptionPlanPolicy[]

export const membershipFeatureLabels: Record<MembershipFeature, string> = {
  communityView: 'Yüksek çözünürlüklü çıktı',
  communityPublish: 'Filigransız dışa aktarım',
  inAppRuntime: 'Tüm prodüksiyon modları & stiller',
  download: 'Doğal dille çok turlu revizyon',
  outsideApp: 'Tüm formatlar (Kare, Dikey, Sinematik)',
  priorityQueue: 'Öncelikli görsel üretim kuyruğu',
  earlyAccess: 'Yeni görsel araçlarına erken erişim',
}

export const premiumImageBenefits = [
  'Premium stiller',
  'Yüksek çözünürlüklü görsel üretimi',
  '90’dan fazla özel filtre',
  'Premium düzenleme araçları',
] as const

export function generationBenefitLabel(plan: MembershipPlan): string {
  if (plan === 'studio') return '200 profesyonel görsel üretimi / ay'
  if (plan === 'pro' || plan === 'plus') return '75 yüksek kalite görsel üretimi / ay'
  return '3 ücretsiz AI görsel üretimi · tek seferlik'
}

const publicPlanOrder: MembershipPlan[] = ['free', 'pro', 'studio']

function publicPlan(plan: MembershipPlan): MembershipPlan {
  return plan === 'plus' ? 'pro' : plan
}

export function subscriptionPlan(
  plan: MembershipPlan,
): SubscriptionPlanPolicy {
  return (
    subscriptionPlans.find((candidate) => candidate.id === plan) ??
    subscriptionPlans[0]
  )
}

export function monthlyGenerationLimit(plan: MembershipPlan): number {
  return subscriptionPlan(plan).monthlyGenerations
}

export function hasMembershipFeature(
  plan: MembershipPlan,
  feature: MembershipFeature,
): boolean {
  return subscriptionPlan(plan).features.includes(feature)
}

export function minimumPlanForFeature(
  feature: MembershipFeature,
): MembershipPlan {
  return (
    subscriptionPlans.find((plan) => plan.id !== 'plus' &&
      (plan.features as readonly MembershipFeature[]).includes(feature),
    )?.id ??
    'studio'
  )
}

export function nextMembershipPlan(
  currentPlan: MembershipPlan,
): MembershipPlan | null {
  const currentIndex = publicPlanOrder.indexOf(publicPlan(currentPlan))
  return publicPlanOrder[currentIndex + 1] ?? null
}

export function isPlanAtLeast(
  plan: MembershipPlan,
  minimum: MembershipPlan,
): boolean {
  return publicPlanOrder.indexOf(publicPlan(plan)) >=
    publicPlanOrder.indexOf(publicPlan(minimum))
}

export function formatUsdPrice(value: number): string {
  if (value === 0) return 'Ücretsiz'
  return `$${value.toFixed(2)}`
}

export function featureUpgradeCopy(
  currentPlan: MembershipPlan,
  feature: MembershipFeature,
) {
  const requiredPlan = minimumPlanForFeature(feature)
  const required = subscriptionPlan(requiredPlan)
  return {
    requiredPlan,
    title: `${required.name} üyeliği gerekli`,
    description: `${membershipFeatureLabels[feature]} özelliği ${required.name} ve üstü üyeliklerde kullanılabilir. ${required.name} planına geçerek ${generationBenefitLabel(requiredPlan)} hakkına erişebilirsin.`,
  }
}

export function quotaUpgradeCopy(currentPlan: MembershipPlan) {
  const nextPlan = nextMembershipPlan(currentPlan)
  if (!nextPlan) {
    return {
      requiredPlan: null,
      title: 'Aylık üretim hakkın doldu',
      description:
        'Studio planındaki aylık 200 profesyonel görsel üretim hakkını kullandın. Yeni dönem başlangıcını üyelik bilgilerinden takip edebilirsin.',
    }
  }
  const next = subscriptionPlan(nextPlan)
  return {
    requiredPlan: nextPlan,
    title: currentPlan === 'free' ? 'Görsel üretim hakkın doldu' : 'Aylık üretim hakkın doldu',
    description: `${subscriptionPlan(currentPlan).name} planındaki görsel hakkını kullandın. ${next.name} planı ${generationBenefitLabel(nextPlan)} sunar.`,
  }
}
